import { AllocationDetails } from '../../components/AllocationEditor';
import { useState, useEffect, useCallback } from 'react';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import DataTable from '../../components/DataTable';
import PeriodPicker from '../../components/PeriodPicker';
import Combobox from '../../components/Combobox';
import MonthSelect from '../../components/MonthSelect';

const ALL_ROWS_LIMIT = 20000;

const SOURCE_LABELS = { request: 'Заявка', bank: 'Банк', manual: 'Ручной ввод' };
const METHOD_LABELS = { exact_rule: 'Точное правило', similarity: 'По аналогии', manual: 'Вручную' };
const OPERATION_TYPE_LABELS = { приход: 'Приход', поступление: 'Поступление', расход: 'Расход', выбытие: 'Выбытие' };
const money = (n) => `${parseFloat(n).toLocaleString('ru')} ₽`;
const monthLabel = (d) => d ? new Date(d).toLocaleDateString('ru', { year: 'numeric', month: 'long' }) : '—';
// Операция — точная классификация из справочника статей (Приход/Поступление/Расход/Выбытие);
// пока статья не проставлена, показываем сырое направление движения денег (Приход/Расход).
const operationLabel = (r) => OPERATION_TYPE_LABELS[r.operation_type] || (r.direction === 'income' ? 'Приход' : 'Расход');

function EntryForm({ meta, onClose, onSaved }) {
  const [direction, setDirection] = useState('expense');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [articleId, setArticleId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const articlesForDirection = meta.articles.filter(a => a.op_type === direction);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!amount || !articleId) { setError('Заполните обязательные поля'); return; }
    setSaving(true);
    try {
      await api.post('/finance/cashflow', {
        entry_date: entryDate, direction, amount: parseFloat(amount), article_id: Number(articleId),
        project_id: projectId || null, contractor_name: contractorName || null, note: note || null
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Новая запись ДДС</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="form-group">
              <label>Тип *</label>
              <select value={direction} onChange={e => { setDirection(e.target.value); setArticleId(''); }}>
                <option value="expense">Расход</option>
                <option value="income">Приход</option>
              </select>
            </div>
            <div className="form-group">
              <label>Дата *</label>
              <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} required />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Статья *</label>
              <Combobox
                value={articleId}
                onChange={setArticleId}
                options={articlesForDirection.map(a => ({ value: a.id, label: `${a.section_name} / ${a.name} (${OPERATION_TYPE_LABELS[a.operation_type] || '—'})` }))}
                placeholder="Начните вводить название статьи"
              />
            </div>
            <div className="form-group">
              <label>Проект</label>
              <Combobox
                value={projectId}
                onChange={setProjectId}
                options={meta.projects.map(p => ({ value: p.id, label: p.name }))}
                placeholder="Без привязки"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Сумма, ₽ *</label>
              <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Контрагент</label>
              <input value={contractorName} onChange={e => setContractorName(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Примечание</label>
            <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkEditPanel({ meta, selected, onClose, onDone }) {
  const [articleId, setArticleId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const apply = async () => {
    setError('');
    const payload = { ids: selected };
    if (articleId !== '') payload.article_id = Number(articleId);
    if (projectId !== '') payload.project_id = Number(projectId);
    if (reportingPeriod !== '') payload.reporting_period = `${reportingPeriod}-01`;
    if (payload.article_id === undefined && payload.project_id === undefined && payload.reporting_period === undefined) {
      setError('Укажите, что изменить');
      return;
    }
    setSaving(true);
    try {
      await api.patch('/finance/cashflow/bulk', payload);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="toolbar" style={{ background: '#f5f5f5', padding: 8, borderRadius: 6, marginBottom: 8 }}>
      <span>Выбрано: {selected.length}</span>
      <div style={{ width: 240 }}>
        <Combobox value={articleId} onChange={setArticleId} options={meta.articles.map(a => ({ value: a.id, label: `${a.section_name} / ${a.name} (${OPERATION_TYPE_LABELS[a.operation_type] || '—'})` }))} placeholder="Статья — не менять" />
      </div>
      <div style={{ width: 200 }}>
        <Combobox value={projectId} onChange={setProjectId} options={meta.projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Проект — не менять" />
      </div>
      <MonthSelect value={reportingPeriod} onChange={setReportingPeriod} />
      {error && <span className="error-msg">{error}</span>}
      <button className="btn-primary btn-sm" disabled={saving} onClick={apply}>{saving ? 'Сохранение...' : 'Применить'}</button>
      <button className="btn-secondary btn-sm" onClick={onClose}>Отмена</button>
    </div>
  );
}

function EntryDetail({ id, meta, onClose, onChanged }) {
  const [entry, setEntry] = useState(null);
  const [articleId, setArticleId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [reportingPeriod, setReportingPeriod] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/finance/cashflow/${id}`).then(r => {
      setEntry(r.data);
      setArticleId(r.data.article_id || '');
      setProjectId(r.data.project_id || '');
      setReportingPeriod(r.data.reporting_period ? r.data.reporting_period.slice(0, 7) : '');
      setNote(r.data.note || '');
    });
  }, [id]);

  const save = async () => {
    setError('');
    setSaving(true);
    try {
      await api.patch(`/finance/cashflow/${id}`, {
        article_id: articleId || null,
        project_id: projectId || null,
        reporting_period: reportingPeriod ? `${reportingPeriod}-01` : null,
        note: note || null
      });
      onChanged();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  if (!entry) return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">Загрузка...</div>
    </div>
  );

  const articlesForDirection = meta.articles.filter(a => a.op_type === entry.direction);

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Операция ДДС #{entry.id}</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {error && <div className="error-msg">{error}</div>}
        <div className="form-row">
          <div className="form-group">
            <label>Дата</label>
            <div>{new Date(entry.entry_date).toLocaleDateString('ru')}</div>
          </div>
          <div className="form-group">
            <label>Операция</label>
            <div>{operationLabel(entry)}</div>
          </div>
          <div className="form-group">
            <label>Сумма</label>
            <div>{money(entry.amount)}</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Контрагент</label>
            <div>{entry.contractor_name || '—'}{entry.contractor_inn ? ` (ИНН ${entry.contractor_inn})` : ''}</div>
          </div>
          <div className="form-group">
            <label>Счёт</label>
            <div>{entry.bank_account_name || '—'}</div>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Статья</label>
            <Combobox value={articleId} onChange={setArticleId} options={articlesForDirection.map(a => ({ value: a.id, label: `${a.section_name} / ${a.name} (${OPERATION_TYPE_LABELS[a.operation_type] || '—'})` }))} placeholder="Выберите статью" />
          </div>
          <div className="form-group">
            <label>Проект</label>
            <Combobox value={projectId} onChange={setProjectId} options={meta.projects.map(p => ({ value: p.id, label: p.name }))} placeholder="Без привязки" />
          </div>
        </div>
        <div className="form-group">
          <label>Отчётный период</label>
          <MonthSelect value={reportingPeriod} onChange={setReportingPeriod} />
        </div>
        <div className="form-group">
          <label>Примечание</label>
          <textarea rows={2} value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <AllocationDetails rows={entry.allocations}/>
        {entry.logs && entry.logs.length > 0 && (
          <div className="form-group">
            <label>История изменений</label>
            <div className="text-muted" style={{ fontSize: 12, maxHeight: 120, overflowY: 'auto' }}>
              {entry.logs.map(l => (
                <div key={l.id}>
                  {new Date(l.created_at).toLocaleString('ru')} — {l.action}{l.user_name ? ` (${l.user_name})` : l.was_automatic ? ' (автоматически)' : ''}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose}>Закрыть</button>
          <button type="button" className="btn-primary" disabled={saving} onClick={save}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Cashflow() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // DataTable хранит выбор чекбоксов и свои внутренние фильтры по столбцам в собственном
  // состоянии — размонтировать её при каждой перезагрузке нельзя, иначе это состояние слетает.
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [filters, setFilters] = useState({ direction: '', source_type: '' });
  const [period, setPeriod] = useState({ from: null, to: null });
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState([]);
  const [showBulk, setShowBulk] = useState(false);
  const [openId, setOpenId] = useState(null);

  const canEdit = user.role === 'admin' || user.role === 'accountant';

  useEffect(() => { api.get('/finance/meta').then(r => setMeta(r.data)); }, []);

  const load = useCallback(async () => {
    try {
      const params = { ...filters, date_from: period.from, date_to: period.to, page: 1, limit: ALL_ROWS_LIMIT };
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);
      const { data } = await api.get('/finance/cashflow', { params });
      setRows(data.data); setTotal(data.total);
    } finally { setLoadedOnce(true); }
  }, [filters, period]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const remove = async (id) => {
    if (!window.confirm('Удалить запись из ленты ДДС?')) return;
    await api.delete(`/finance/cashflow/${id}`);
    load();
  };

  const columns = [
    { key: 'entry_date', label: 'Дата', render: r => new Date(r.entry_date).toLocaleDateString('ru'), sortValue: r => r.entry_date, filterable: false },
    { key: 'reporting_period', label: 'Отчётный период', render: r => monthLabel(r.reporting_period), sortValue: r => r.reporting_period, filterValue: r => monthLabel(r.reporting_period) },
    { key: 'operation_type', label: 'Операция', render: r => <span className={`badge ${r.direction === 'income' ? 'badge-paid' : 'badge-new'}`}>{operationLabel(r)}</span>, filterValue: r => operationLabel(r) },
    { key: 'article_name', label: 'Статья', render: r => r.allocations?.length ? `Разбивка: ${r.allocations.length} ст.` : r.article_name || '—' },
    { key: 'section_name', label: 'Раздел', render: r => r.section_name || '—' },
    { key: 'amount', label: 'Сумма', render: r => money(r.amount), sortValue: r => Number(r.amount), filterable: false },
    { key: 'contractor_name', label: 'Контрагент', render: r => r.contractor_name || '—' },
    { key: 'project_name', label: 'Проект', render: r => r.project_name || '—' },
    { key: 'bank_account_name', label: 'Счёт', render: r => r.bank_account_name || '—' },
    { key: 'evidence_status', label: 'Подтверждение', render: r => r.evidence_status === 'unverified' ? 'Требует сверки (не в итогах)' : r.evidence_status === 'bank' ? 'Банк' : 'Кассовый документ' },
    { key: 'source_type', label: 'Источник', render: r => SOURCE_LABELS[r.source_type], filterValue: r => SOURCE_LABELS[r.source_type] },
    { key: 'categorization_method', label: 'Категоризация', render: r => METHOD_LABELS[r.categorization_method] || '—', filterValue: r => METHOD_LABELS[r.categorization_method] || '' },
    
  ];

  return (
    <div>
      <div className="card mb-2">Банк включает все загруженные движения, даже без статьи. Заявления об оплате видны для сверки и не увеличивают подтверждённые денежные итоги. Ошибочные записи аннулируются с причиной в разделе «Начисления и сверка».</div>
      <div className="toolbar">
        <select value={filters.direction} onChange={e => setFilter('direction', e.target.value)}>
          <option value="">Приход и расход</option>
          <option value="income">Приход</option>
          <option value="expense">Расход</option>
        </select>
        <select value={filters.source_type} onChange={e => setFilter('source_type', e.target.value)}>
          <option value="">Все источники</option>
          <option value="request">Заявки</option>
          <option value="bank">Банк</option>
          <option value="manual">Ручной ввод</option>
        </select>
        <PeriodPicker onChange={setPeriod} />
        <div className="toolbar-spacer" />
        {canEdit && selected.length > 0 && (
          <button className="btn-secondary btn-sm" onClick={() => setShowBulk(true)}>Изменить выбранные ({selected.length})</button>
        )}
        <button className="btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Запись ДДС</button>
      </div>

      {showBulk && meta && selected.length > 0 && (
        <BulkEditPanel meta={meta} selected={selected} onClose={() => setShowBulk(false)} onDone={() => { setShowBulk(false); setSelected([]); load(); }} />
      )}

      {!loadedOnce ? <div className="loading-text">Загрузка...</div> : (
        <DataTable
          columns={columns}
          rows={rows}
          initialSort={{ key: 'entry_date', dir: 'desc' }}
          onRowClick={row => setOpenId(row.id)}
          emptyText="Записей не найдено"
          selectedIds={canEdit ? selected : undefined}
          onSelectedChange={canEdit ? setSelected : undefined}
          canExport={canEdit}
          exportFilename="ДДС"
        />
      )}
      {loadedOnce && total > rows.length && <div className="text-muted" style={{ fontSize: 12 }}>Показаны первые {rows.length} из {total} — сузьте период или фильтр.</div>}

      {showForm && meta && <EntryForm meta={meta} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {openId && meta && <EntryDetail id={openId} meta={meta} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
